import React, { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useMedicalProfileData from '../../../../../hooks/api/useMedicalProfileData';
import { getIntl } from '../../../../../utils/intl';
import ResourceSearch from '../../../../shared/ResourceSearch/ResourceSearch';
import ResourceCard from '../../../../shared/ResourceCard';
import { categoryToDomain } from '../../../../../utils/resourceUtils/medicalDataPermissions';
// import { SearchFilterValue } from '../../../../shared/ResourceSearch/ResourceListSearchFilters';
import { APITypes, MedicalDataTypes } from '../../../../../primary-record-types';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import {
    SearchFilterValue,
    SearchInputRecord,
    filterValueToScreenParams,
} from '../../../../shared/ResourceSearch/ResourceListSearchFilters';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import { useMaybeRenderedSideDrawerToggle } from '../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import VisitInviteEmailSideDrawerContent from '../../../../shared/VisitInviteEmailSideDrawerContent';
import urls from '../../../../../utils/urls';
import { appAPI } from '../../../../../utils/api';
import { getMedicalProfileDetailsCacheKey } from '../../../../../hooks/api/cacheKeys';
import { onProfileContactDetailsUpdate } from '../../../../../hooks/api/cacheKeys';
import Button from '../../../../v2BaseComponents/Button';
import Text from '../../../../v2BaseComponents/Text';

const intl = getIntl('standard-resource-list', {
    add: 'Add',
    bulkEdit: 'Bulk Edit',
    subscribeToVisit: 'Subscribe to Visits',
    subscribe: 'Subscribe',
    noKnownDrugAllergies: 'No Known Drug Allergies',
    noKnownAllergies: 'No Known Allergies',
});

export type StandardResourceListOwnProps = {
    onSearchFilterUpdate?: (filter: SearchFilterValue, count: number) => void;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    medicalProfileId: string;
};

export type StandardResourceListProps = StandardResourceListOwnProps;

const StandardResourceList: React.FC<StandardResourceListProps> = ({
    resourceCategory,
    medicalProfileId,
    onSearchFilterUpdate,
}) => {
    const navigation = useProfilesTabNavigation();
    const queryClient = useQueryClient();
    const { groupedResources, isLoadingResources } = useMedicalProfileData({
        medicalProfileId,
        resourceCategory,
    });
    const isAllergyCategory = resourceCategory === 'allergy';
    const profileQuery = useQuery({
        queryKey: getMedicalProfileDetailsCacheKey(medicalProfileId),
        queryFn: async () =>
            await appAPI.get<APITypes.medicalProfileService.GetProfileResponse>(
                urls.api.medicalProfiles.medicalProfile(medicalProfileId),
            ),
        enabled: isAllergyCategory && !!medicalProfileId,
    });
    const noKnownDrugAllergies = profileQuery.data?.noKnownDrugAllergies ?? false;
    const setNoKnownDrugAllergiesMutation = useMutation({
        mutationFn: async () => {
            await appAPI.put(urls.api.medicalProfiles.noKnownDrugAllergies(medicalProfileId));
        },
        onSuccess: () => {
            onProfileContactDetailsUpdate.resetCache({ medicalProfileId })();
            void queryClient.invalidateQueries({ queryKey: getMedicalProfileDetailsCacheKey(medicalProfileId) });
        },
    });
    const profileVisitInviteSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'profile-visit-invite',
        title: intl('subscribeToVisit'),
        renderContent: (onClose) => (
            <VisitInviteEmailSideDrawerContent medicalProfileId={medicalProfileId} onClose={onClose} />
        ),
        mobileOnFocus: () =>
            navigation.navigate('visit-invite-emails', {
                profileId: medicalProfileId,
            }),
    });
    const canEdit = useHasPermission(medicalProfileId, categoryToDomain(resourceCategory), 'edit');
    const [trackedSearchFilterState, setTrackedSearchFilterState] = React.useState<SearchFilterValue | null>(null);
    const record = useRecordEvent();
    const renderFilteredResources = useCallback(
        (resource: SearchInputRecord) => {
            return (
                <ResourceCard
                    resource={resource}
                    category={resourceCategory}
                    profileId={medicalProfileId}
                    onPress={() => {
                        navigation.navigate('data-details', {
                            profileId: medicalProfileId,
                            category: resourceCategory,
                            resourceId: resource.id,
                        });
                    }}
                />
            );
        },
        [medicalProfileId, navigation, resourceCategory],
    );

    return (
        <>
            <ActionButtonLayout
                actions={[
                    {
                        icon: 'PlusCircle',
                        title: intl('add'),
                        onPress: () => {
                            navigation.navigate('add-data', {
                                profileId: medicalProfileId,
                                category: resourceCategory,
                                sourceResourceId: null,
                            });
                        },
                        testID: 'add-resource-button',
                        show: canEdit,
                    },
                    {
                        icon: 'Calendar',
                        title: intl('subscribe'),
                        onPress: () => {
                            profileVisitInviteSideDrawer.onOpen();
                        },
                        testID: 'subscribe-visit-button',
                        show: resourceCategory === 'visit',
                    },
                    {
                        icon: 'Edit2',
                        title: intl('bulkEdit'),
                        onPress: () => {
                            record('bulkEdit');
                            navigation.navigate('data-bulk-update', {
                                profileId: medicalProfileId,
                                category: resourceCategory,
                                ...(trackedSearchFilterState
                                    ? filterValueToScreenParams(trackedSearchFilterState, resourceCategory)
                                    : {}),
                            });
                        },
                        testID: 'bulk-update-resource-button',
                        show: canEdit,
                    },
                ]}
            >
                <AnimateLoadedContent isLoading={isLoadingResources}>
                    {() => (
                        <ResourceSearch
                            category={resourceCategory}
                            resources={groupedResources || []}
                            isLoadingResources={isLoadingResources}
                            onSearchFilterUpdate={(searchValue, count) => {
                                setTrackedSearchFilterState(searchValue);
                                onSearchFilterUpdate?.(searchValue, count);
                            }}
                            renderFilteredResources={renderFilteredResources}
                            emptyStateHideDefaultHeader={
                                isAllergyCategory &&
                                noKnownDrugAllergies &&
                                !profileQuery.isPending &&
                                (groupedResources?.length ?? 0) === 0
                            }
                            emptyStateRenderNoKnownDrugAllergies={
                                isAllergyCategory &&
                                !profileQuery.isPending &&
                                (groupedResources?.length ?? 0) === 0 ? (
                                    noKnownDrugAllergies ? (
                                        <Text variant="bold-h4" alignText="center" wrapText>
                                            {intl('noKnownAllergies')}
                                        </Text>
                                    ) : canEdit ? (
                                        <Button
                                            icon="CheckCircle"
                                            title={intl('noKnownDrugAllergies')}
                                            onPress={() => setNoKnownDrugAllergiesMutation.mutate()}
                                            testID="no-known-drug-allergies-button"
                                            isDisabled={setNoKnownDrugAllergiesMutation.isPending}
                                            size="lg"
                                            variant="primary"
                                            onLayout={() => {}}
                                        />
                                    ) : null
                                ) : undefined
                            }
                        />
                    )}
                </AnimateLoadedContent>
            </ActionButtonLayout>
            {profileVisitInviteSideDrawer.SideDrawer}
        </>
    );
};

export default React.memo(StandardResourceList);
